import type {
  SitesWeatherCurrentObservations,
  SitesWeatherDailyForecast,
  SitesWeatherUvForecast,
} from "@ihr-radioedit/inferno-webapi";

/**
 * Weather data is now delivered through the RadioEdit WebAPI (WeatherBug) as a `forecast`
 * union on the weather page block — see IHRWEB-23972 / IHRAR-11218. Inferno no longer calls
 * the weather provider directly, so this module only carries the shared types and the small
 * display helpers the weather components need.
 */

export type CurrentObservations = { __typename: "SitesWeatherCurrentObservations" } & Pick<
  SitesWeatherCurrentObservations,
  "iconCode" | "iconDescription" | "temperature" | "humidity" | "pressure" | "windCurrentSpeed" | "windCurrentDirection"
>;

export type DailyForecast = Pick<
  SitesWeatherDailyForecast,
  | "forecastDateLcl"
  | "iconCode"
  | "iconDescription"
  | "temperatureHigh"
  | "temperatureLow"
  | "precipProbabilityPercent100"
>;

export type DailyForecastList = { __typename: "SitesWeatherDailyForecastList"; days: DailyForecast[] };

export type UvForecast = { __typename: "SitesWeatherUvForecast" } & Pick<
  SitesWeatherUvForecast,
  "forecastDateLcl" | "uvIndex" | "uvLabel"
>;

export type WeatherForecast = CurrentObservations | DailyForecastList | UvForecast | null | undefined;

const CARDINALS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];

/** Convert a wind direction in degrees (WeatherBug `windCurrentDirection`) to a cardinal label. */
export const degreesToCardinal = (degrees?: number | null): string => {
  if (degrees === null || degrees === undefined) {
    return "";
  }
  const index = Math.round((degrees % 360) / 22.5) % 16;
  return CARDINALS[(index + 16) % 16];
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Derive a 3-letter English weekday abbreviation from a WeatherBug `forecastDateLcl` value.
 * The abbreviation is passed through i18n `t()` by the caller, preserving the prior behavior
 * where the day-of-week string was the translation key.
 *
 * `forecastDateLcl` is already a local datetime (e.g. "2021-07-06T07:00:00-0400"), so we use
 * its calendar-date portion directly — computing the weekday in UTC keeps the result stable
 * regardless of the runtime timezone.
 */
export const weekdayAbbrev = (forecastDateLcl?: string | null): string => {
  if (!forecastDateLcl) {
    return "";
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(forecastDateLcl);
  if (!match) {
    return "";
  }
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (isNaN(date.getTime())) {
    return "";
  }
  return WEEKDAYS[date.getUTCDay()] || "";
};
